import { Check, Download, AlertCircle, Loader2 } from "lucide-react";
import type { FC } from "react";
import { useState, useEffect } from "react";

interface IImportComplete {
  resetImport: () => void;
  cancelImport: () => void;
  csvData: unknown[] | null;
  importedRowCount?: number;
}
const ImportComplete: FC<IImportComplete> = function ({
  resetImport,
  cancelImport,
  csvData,
  importedRowCount = 0,
}) {
  const [processingDelay, setProcessingDelay] = useState(true);

  // Simulate processing delay notification
  useEffect(() => {
    const timer = setTimeout(() => {
      setProcessingDelay(false);
    }, 3000); // Show processing message for 3 seconds

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="text-center py-12">
      {processingDelay ? (
        <>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Processing Import...
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            Your leads are being processed and will appear in the system shortly.
            Successfully queued {importedRowCount || csvData?.length || 0} leads for import.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-medium text-blue-900">Please Note</p>
                <p className="text-xs text-blue-700 mt-1">
                  Leads may take a few moments to appear in your leads list as they are being validated and processed by the system.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Import Complete!
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            Successfully imported {importedRowCount || csvData?.length || 0} rows of data
          </p>
          <p className="text-xs text-gray-500 mb-6">
            Your leads should now be visible in the leads list. If they don't appear immediately, please refresh the page.
          </p>
        </>
      )}

      <div className="space-x-4 flex items-center justify-center mt-6">
        <button
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          onClick={resetImport}
          disabled={processingDelay}
        >
          <Download className="h-4 w-4 mr-2" />
          Import Another File
        </button>

        <button
          className="inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium border-gray-300 text-gray-700"
          onClick={cancelImport}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ImportComplete;
